
import React, { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { UserX, Clock, Mail } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';
import { format } from 'date-fns';

interface SuspendedAccountScreenProps {
  suspendedUntil?: string | null;
  suspensionReason?: string | null;
}

const SuspendedAccountScreen: React.FC<SuspendedAccountScreenProps> = ({
  suspendedUntil,
  suspensionReason
}) => {
  const navigate = useNavigate();
  const { signOut, profile } = useAuth();
  const pollingRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    if (profile && profile.status === 'active') {
      navigate('/dashboard', { replace: true });
    }
  }, [profile?.status]);

  useEffect(() => {
    const checkStatus = async () => {
      try {
        const { data } = await supabase.rpc('get_user_security_status');
        const status = (data as any)?.status;
        if (status === 'active') {
          if (pollingRef.current) clearInterval(pollingRef.current);
          navigate('/dashboard', { replace: true });
        }
      } catch (err) {
        console.error('[SuspendedScreen] Status check failed:', err);
      }
    };
    checkStatus();
    pollingRef.current = setInterval(checkStatus, 10000);
    return () => {
      if (pollingRef.current) clearInterval(pollingRef.current);
    };
  }, []);

  const handleSignOut = () => {
    signOut();
  };

  const formatSuspensionEnd = (dateString: string) => {
    try {
      return format(new Date(dateString), 'dd MMMM yyyy HH:mm');
    } catch { return dateString; }
  };

  const isTemporarySuspension = suspendedUntil && new Date(suspendedUntil) > new Date();

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-red-100">
            <UserX className="h-6 w-6 text-red-600" />
          </div>
          <CardTitle className="text-xl font-semibold text-gray-900">
            Account Suspended
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="text-center text-gray-600">
            <p className="mb-4">
              Your account has been {isTemporarySuspension ? 'temporarily' : 'permanently'} suspended and you cannot access the system at this time.
            </p>
            
            {suspensionReason && (
              <div className="mb-4 p-3 bg-gray-100 rounded-lg">
                <p className="text-sm font-medium text-gray-700 mb-1">Reason:</p>
                <p className="text-sm text-gray-600">{suspensionReason}</p>
              </div>
            )}

            {isTemporarySuspension ? (
              <div className="flex items-center justify-center text-sm text-gray-500 mb-4">
                <Clock className="h-4 w-4 mr-2" />
                <span>Suspension ends: {formatSuspensionEnd(suspendedUntil!)}</span>
              </div>
            ) : (
              <div className="flex items-center justify-center text-sm text-gray-500 mb-4">
                <UserX className="h-4 w-4 mr-2" />
                <span>This suspension is permanent</span>
              </div>
            )}

            <div className="flex items-center justify-center text-sm text-gray-500 mb-6">
              <Mail className="h-4 w-4 mr-2" />
              <span>Contact your administrator for assistance</span>
            </div>
          </div>

          <Button 
            onClick={handleSignOut}
            variant="outline" 
            className="w-full"
          >
            Sign Out
          </Button>
        </CardContent>
      </Card>
    </div>
  );
};

export default SuspendedAccountScreen;
